import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  addStoreMappedCartItems,
  connectStore,
  deleteStoreMapping,
  disconnectStore,
  fetchPlazaVeaCoverage,
  fetchPlazaVeaLocation,
  fetchStoreCart,
  fetchStoreMappings,
  fetchStoreStatus,
  fetchWongLocation,
  removeStoreCartItem,
  saveStoreMapping,
  setPlazaVeaLocation,
  setStoreCartItemQuantity,
  setStoreCartSkuQuantity,
  setWongLocation,
  type StoreCartAddItem,
  type StoreMapping,
} from "@/lib/api";
import type { StoreId } from "@/lib/stores";

// Every key is scoped by store so switching Wong ⇄ PlazaVea swaps caches
// instead of showing one store's cart/links under the other's name.
export const storeQueryKeys = {
  all: (store: StoreId) => ["store", store] as const,
  status: (store: StoreId) => [...storeQueryKeys.all(store), "status"] as const,
  coverage: () => [...storeQueryKeys.all("plazavea"), "coverage"] as const,
  location: (store: StoreId) => [...storeQueryKeys.all(store), "location"] as const,
  mappings: (store: StoreId) => [...storeQueryKeys.all(store), "mappings"] as const,
  carts: (store: StoreId) => [...storeQueryKeys.all(store), "cart"] as const,
  cart: (store: StoreId, weekId: string) => [...storeQueryKeys.all(store), "cart", weekId] as const,
};

export function useStoreStatus(store: StoreId) {
  return useQuery({
    queryKey: storeQueryKeys.status(store),
    queryFn: () => fetchStoreStatus(store),
    staleTime: 60_000,
  });
}

export function usePlazaVeaCoverage(enabled: boolean) {
  return useQuery({
    queryKey: storeQueryKeys.coverage(),
    queryFn: fetchPlazaVeaCoverage,
    enabled,
    staleTime: 60 * 60_000,
    gcTime: 24 * 60 * 60_000,
  });
}

export function usePlazaVeaLocation(enabled: boolean) {
  return useQuery({
    queryKey: storeQueryKeys.location("plazavea"),
    queryFn: fetchPlazaVeaLocation,
    enabled,
    staleTime: 60_000,
  });
}

export function useWongLocation(enabled: boolean) {
  return useQuery({
    queryKey: storeQueryKeys.location("wong"),
    queryFn: fetchWongLocation,
    enabled,
    staleTime: 60_000,
  });
}

export function useStoreMappings(store: StoreId, enabled: boolean) {
  return useQuery({
    queryKey: storeQueryKeys.mappings(store),
    queryFn: () => fetchStoreMappings(store),
    enabled,
    staleTime: 30_000,
  });
}

export function useStoreCart(store: StoreId, weekId: string, enabled: boolean) {
  return useQuery({
    queryKey: storeQueryKeys.cart(store, weekId),
    queryFn: () => fetchStoreCart(store, weekId),
    enabled: enabled && !!weekId,
    staleTime: 15_000,
    refetchOnWindowFocus: true,
  });
}

export function useConnectStore(store: StoreId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ email, password }: { email: string; password: string }) =>
      connectStore(store, email, password),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: storeQueryKeys.all(store) });
    },
  });
}

export function useDisconnectStore(store: StoreId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => disconnectStore(store),
    onSuccess: () => {
      qc.removeQueries({ queryKey: storeQueryKeys.carts(store) });
      qc.removeQueries({ queryKey: storeQueryKeys.location(store) });
      if (store === "plazavea") qc.removeQueries({ queryKey: storeQueryKeys.coverage() });
      // Links survive a disconnect (they're DB-side), so only refresh them.
      void qc.invalidateQueries({ queryKey: storeQueryKeys.mappings(store) });
      void qc.invalidateQueries({ queryKey: storeQueryKeys.status(store) });
    },
  });
}

export function useSaveStoreMapping(store: StoreId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Parameters<typeof saveStoreMapping>[1]) =>
      saveStoreMapping(store, input),
    onSuccess: (saved) => {
      qc.setQueryData<StoreMapping[]>(storeQueryKeys.mappings(store), (prev) => {
        const list = prev ?? [];
        const without = list.filter((m) => m.ingredientName !== saved.ingredientName);
        return [...without, saved];
      });
      void qc.invalidateQueries({ queryKey: storeQueryKeys.mappings(store) });
    },
  });
}

export function useDeleteStoreMapping(store: StoreId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ingredientName: string) => deleteStoreMapping(store, ingredientName),
    onSuccess: (_void, ingredientName) => {
      qc.setQueryData<StoreMapping[]>(storeQueryKeys.mappings(store), (prev) =>
        (prev ?? []).filter((m) => m.ingredientName !== ingredientName),
      );
      void qc.invalidateQueries({ queryKey: storeQueryKeys.mappings(store) });
    },
  });
}

export function useSetPlazaVeaLocation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Parameters<typeof setPlazaVeaLocation>[0]) =>
      setPlazaVeaLocation(input),
    onSuccess: (location) => {
      qc.setQueryData(storeQueryKeys.location("plazavea"), location);
      // Address changed on every stored week-cart in the Worker, so invalidate all.
      void qc.invalidateQueries({ queryKey: storeQueryKeys.carts("plazavea") });
    },
  });
}

export function useSetWongLocation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (addressId: string) => setWongLocation(addressId),
    onSuccess: (state) => {
      qc.setQueryData(storeQueryKeys.location("wong"), state);
      // Address changed on every stored week-cart in the Worker, so invalidate all.
      void qc.invalidateQueries({ queryKey: storeQueryKeys.carts("wong") });
    },
  });
}

export function useAddStoreMappedCartItems(store: StoreId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ weekId, items }: { weekId: string; items: StoreCartAddItem[] }) =>
      addStoreMappedCartItems(store, weekId, items),
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({ queryKey: storeQueryKeys.cart(store, variables.weekId) });
    },
  });
}

export function useRemoveStoreCartItem(store: StoreId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ weekId, skuId, sellerId }: { weekId: string; skuId: string; sellerId?: string }) =>
      removeStoreCartItem(store, weekId, skuId, sellerId),
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({ queryKey: storeQueryKeys.cart(store, variables.weekId) });
    },
  });
}

export function useSetStoreCartItemQuantity(store: StoreId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      weekId,
      ingredientName,
      totalGrams,
      totalUnits,
    }: { weekId: string; ingredientName: string; totalGrams: number; totalUnits: number }) =>
      setStoreCartItemQuantity(store, weekId, ingredientName, totalGrams, totalUnits),
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({ queryKey: storeQueryKeys.cart(store, variables.weekId) });
    },
  });
}

export function useSetStoreCartSkuQuantity(store: StoreId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      weekId,
      skuId,
      quantity,
      sellerId,
    }: { weekId: string; skuId: string; quantity: number; sellerId?: string }) =>
      setStoreCartSkuQuantity(store, weekId, skuId, quantity, sellerId),
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({ queryKey: storeQueryKeys.cart(store, variables.weekId) });
    },
  });
}
